import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Send, Trash2, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm";
const REACTIONS = [
  { key: "clap", emoji: "👏", label: "Aplausos" },
  { key: "heart", emoji: "❤️", label: "Me encanta" },
  { key: "thumb", emoji: "👍", label: "Me gusta" },
];

export default function Social() {
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [commentFor, setCommentFor] = useState(null);
  const [comment, setComment] = useState("");
  const viewed = useRef(new Set());

  const load = () =>
    api.get("/px/social/posts").then((r) => {
      setPosts(r.data);
      // Registrar vista única por publicación (una vez por sesión)
      r.data.forEach((p) => {
        if (!viewed.current.has(p.id)) {
          viewed.current.add(p.id);
          api.post(`/px/social/posts/${p.id}/view`).catch(() => {});
        }
      });
    }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const publish = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try { await api.post("/px/social/posts", { text }); setText(""); load(); toast.success("Publicado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setPosting(false); }
  };
  const react = async (p, type) => { try { await api.post(`/px/social/posts/${p.id}/react`, { type }); load(); } catch { /* */ } };
  const sendComment = async (p) => { if (!comment.trim()) return; try { await api.post(`/px/social/posts/${p.id}/comment`, { text: comment }); setComment(""); setCommentFor(null); load(); } catch { /* */ } };
  const del = async (p) => { try { await api.delete(`/px/social/posts/${p.id}`); load(); } catch { /* */ } };

  return (
    <OSLayout title="OMNIA Social">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="social-page">
        <div>
          <div className="overline text-navy/40 mb-2">OMNIA People Suite · Human OS</div>
          <h2 className="font-editorial text-4xl tracking-tight">OMNIA Social</h2>
          <p className="mt-2 text-navy/50">El muro corporativo: comparte, reacciona y comenta con tu organización.</p>
        </div>

        <div className="card-surface p-4">
          <textarea className={input} rows={3} placeholder="¿Qué quieres compartir con tu equipo?" value={text} onChange={(e) => setText(e.target.value)} data-testid="social-input" />
          <div className="flex justify-end mt-3"><button onClick={publish} disabled={posting} className="btn btn-primary" data-testid="social-publish"><Send className="w-4 h-4" /> Publicar</button></div>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          posts.length === 0 ? <div className="card-surface py-12 text-center text-navy/40 text-sm">Sé el primero en publicar.</div> :
          posts.map((p) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-5" data-testid={`social-post-${p.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-gradient-violet text-white flex items-center justify-center text-sm font-medium">{(p.author || "?")[0]}</div>
                <div><div className="font-medium text-sm">{p.author}</div><div className="text-[11px] text-navy/40">{new Date(p.created_at).toLocaleString("es-MX")}</div></div>
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-navy/40" data-testid={`social-views-${p.id}`}><Eye className="w-3.5 h-3.5" /> {p.views?.length || 0}</span>
                <button onClick={() => del(p)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
              <p className="text-sm text-navy/80 whitespace-pre-wrap">{p.text}</p>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {REACTIONS.map((r) => {
                  const count = p.reactions?.[r.key]?.length || 0;
                  return (
                    <button key={r.key} onClick={() => react(p, r.key)} title={r.label}
                      className={`inline-flex items-center gap-1.5 text-sm rounded-full px-2.5 py-1 border transition-colors ${count ? "border-violet/30 bg-violet/5 text-navy" : "border-navy/10 text-navy/50 hover:border-violet/30"}`}
                      data-testid={`social-react-${r.key}-${p.id}`}>
                      <span className="text-base leading-none">{r.emoji}</span> {count}
                    </button>
                  );
                })}
                <button onClick={() => setCommentFor(commentFor === p.id ? null : p.id)} className="inline-flex items-center gap-1.5 text-sm text-navy/50 hover:text-violet ml-1" data-testid={`social-comment-toggle-${p.id}`}><MessageCircle className="w-4 h-4" /> {p.comments?.length || 0}</button>
              </div>
              {p.comments?.length > 0 && <div className="mt-3 space-y-1.5 border-t border-navy/5 pt-3">{p.comments.map((c) => <div key={c.id} className="text-sm"><span className="font-medium">{c.by}: </span><span className="text-navy/70">{c.text}</span></div>)}</div>}
              {commentFor === p.id && (
                <div className="flex gap-2 mt-3">
                  <input className={input} placeholder="Escribe un comentario…" value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment(p)} data-testid={`social-comment-input-${p.id}`} />
                  <button onClick={() => sendComment(p)} className="btn btn-outline text-sm" data-testid={`social-comment-send-${p.id}`}>Enviar</button>
                </div>
              )}
            </motion.div>
          ))}
      </div>
    </OSLayout>
  );
}
